import { useState, useEffect } from "react";
import BASE_URL from "../BASEURL";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Breadcrumb from "../components/Breadcrumb";
import { Link, useLocation } from "react-router-dom";
import { products as staticProducts } from "../data/products";

const getInitialCategories = () => {
  return staticProducts.map((group) => ({
    category: group.categoryName || group.category,
    items: (group.items || []).map((item) => ({
      ...item,
      id: item.id || item._id || item.slug || item.name,
    })),
  }));
};

export default function Products() {
  const location = useLocation();
  const selectedCategory = location.state?.selectedCategory;
  const [categories, setCategories] = useState(getInitialCategories);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (typeof window === "undefined") return;
    const hash = window.location.hash;
    if (hash === "#products-section") {
      setTimeout(() => {
        const element = document.getElementById("products-section");
        if (element) {
          const elementTop = element.getBoundingClientRect().top + window.pageYOffset;
          window.scrollTo({ top: elementTop - 110, behavior: "smooth" });
        }
      }, 200);
    }
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(`${BASE_URL}/api/product`);
        if (!res.ok) throw new Error("Failed to fetch products");
        const json = await res.json();
        
        const grouped = {};
        (json.data || []).forEach((prod) => {
          const cat = (prod.category && prod.category.name) || "Uncategorized";
          if (!grouped[cat]) grouped[cat] = [];
          grouped[cat].push(prod);
        });

        const desiredOrder = [
          "Aeration Equipment",
          "Sludge Handling Systems",
          "Dosing Pumps & Agitators",
          "Filteration Equipment"
        ];

        const itemOrderMap = {
          "aeration equipment": ["Blowers", "Acoustic Hoods", "Tubular Diffusers", "Disc Diffusers", "MBBR Media", "Spare Parts / Accessories"],
          "sludge handling systems": ["Sludge Dewatering", "Filter Press", "Tubesettler Media", "Screw Pump", "Centrifuge Machine"],
          "dosing pumps & agitators": ["Electronic Dosing Pump", "Industrial Agitator / Mixer", "Hydraulic Pump", "Mechanical Pump", "Plunger Pump", "Dosing System"],
          "filteration equipment": ["Vessels", "Pressure Tube", "Float Switch", "Pressure Guage", "Multiport Valve", "Distribution System", "Filter Sand", "Activated Carbon"]
        };

        const arr = [];
        desiredOrder.forEach((cat) => {
          if (grouped[cat]) {
            const items = grouped[cat];
            const key = cat.toLowerCase();
            const desiredItems = itemOrderMap[key];
            if (desiredItems) {
              const idxMap = {};
              desiredItems.forEach((n, i) => (idxMap[n.toLowerCase()] = i));
              items.sort((a, b) => {
                const nameA = (a.name || "").toLowerCase().trim();
                const nameB = (b.name || "").toLowerCase().trim();
                const ia = idxMap.hasOwnProperty(nameA) ? idxMap[nameA] : Infinity;
                const ib = idxMap.hasOwnProperty(nameB) ? idxMap[nameB] : Infinity;
                return ia - ib;
              });
            }
            arr.push({ category: cat, items });
            delete grouped[cat];
          }
        });

        Object.keys(grouped).forEach((cat) => {
          arr.push({ category: cat, items: grouped[cat] });
        });
        setCategories(arr);
      } catch (err) {
        setError(err.message || "Failed to fetch products");
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  return (
    <>
      <Navbar />
      <div className="bg-dark" >
        <Breadcrumb />
      </div>

      <section className="md:block py-6 bg-white" id="products-section">
        <div className="max-w-7xl mx-auto px-4">
          
          <div className="text-center mb-10 " >
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-primary">
              Our Products
            </h1>
          </div>

          {(() => {
            const displayCategories = selectedCategory
              ? categories.filter((cat) => cat.category === selectedCategory.name)
              : categories;

            if (!displayCategories || displayCategories.length === 0) {
              return <div className="text-center text-red-500 col-span-full">No products found.</div>;
            }

            return displayCategories.map((category, index) => (
              <div key={category.category + '-' + index} className="mb-16" id={`cat-${index}`}>
                <h2 className="text-2xl font-semibold text-primary mb-6 border-l-4 border-primary pl-4">
                  {typeof category.category === 'object' ? category.category.name : category.category}
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                  {category.items.map((item) => {
                    const productId = item.id || item._id || item.slug;
                    return (
                      <Link
                        key={`${productId}-${index}`}
                        to={`/product/${productId}`}
                        state={{ product: item }}
                        className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-primary/10 overflow-hidden flex flex-col"
                      >
                        <div className="h-48 flex items-center justify-center bg-gradient-to-br from-primary/5 to-white p-6">
                          <img
                            src={item.image}
                            alt={item.name}
                            className="max-h-40 w-full object-contain group-hover:scale-110 transition duration-500"
                          />
                        </div>
                        <div className="flex-1 p-5 flex flex-col">
                          <h3 className="font-bold text-lg text-primaryDark mb-2 group-hover:text-primary transition-colors">
                            {item.name}
                          </h3>
                          <p className="text-gray-600 text-sm line-clamp-3">
                            {item.shortDesc}
                          </p>
                          <div className="mt-auto pt-4 text-primary font-semibold text-sm group-hover:translate-x-2 transition-transform">
                            View Details →
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            ));
          })()}
        </div>
      </section>

      <Footer />
    </>
  );
}